import { useEffect, useRef, useCallback } from 'react';

type KonamiCallback = () => void;

// Up, Up, Down, Down, Left, Right, Left, Right, B, A
const KONAMI_SEQUENCE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a'
];

function normalizeKey(key: string): string {
  // Keep arrow names as-is, normalize letters to lowercase
  if (key.startsWith('Arrow')) return key;
  return key.toLowerCase();
}

export default function useKonamiCode(onActivate: KonamiCallback): void {
  const positionRef = useRef<number>(0);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in inputs or contenteditable
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target.isContentEditable)
      ) {
        return;
      }

      const expectedKey = KONAMI_SEQUENCE[positionRef.current];
      const pressedKey = normalizeKey(e.key);

      if (pressedKey === expectedKey) {
        positionRef.current += 1;
        if (positionRef.current === KONAMI_SEQUENCE.length) {
          try {
            onActivate();
          } finally {
            positionRef.current = 0;
          }
        }
        return;
      }

      // If mismatch, but the pressed key is the start of the sequence, start over from 1
      if (pressedKey === KONAMI_SEQUENCE[0]) {
        positionRef.current = 1;
      } else {
        positionRef.current = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onActivate]);
}

/**
 * Hook that sets up the Konami code with built-in "rave mode" toggle functionality.
 * Toggles the 'sl-rgb-rave' class on document.body and persists state to localStorage.
 */
export function useKonamiRaveMode(): void {
  const toggleRave = useCallback(() => {
    const body = document.body;
    const isActive = body.classList.toggle('sl-rgb-rave');
    // Store state to localStorage for persistence across reloads
    try {
      localStorage.setItem('sl-rave', isActive ? '1' : '0');
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  useKonamiCode(toggleRave);

  // Restore rave mode from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('sl-rave');
      if (saved === '1') {
        document.body.classList.add('sl-rgb-rave');
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);
}

